import type { InstallerRow } from './planning';

export function applyInstallerRowOrder(rows: InstallerRow[], savedOrder: number[]): InstallerRow[] {
  if (savedOrder.length === 0) return rows;

  const rank = new Map<number, number>(savedOrder.map((id, index) => [id, index]));
  const ordered = [...rows];

  ordered.sort((a, b) => {
    const aRank = rank.get(a.id);
    const bRank = rank.get(b.id);
    const aKnown = aRank !== undefined;
    const bKnown = bRank !== undefined;

    if (aKnown && bKnown) return aRank - bRank;
    if (aKnown) return -1;
    if (bKnown) return 1;
    return 0;
  });

  return ordered;
}

/**
 * Compute the new saved installer order after an explicit drag.
 *
 * The saved order must always represent the FULL roster, decoupled from which
 * rows happen to be on screen (a quiet week, or "Toon alle installateurs" off).
 * Off-screen installers in `savedOrder` are preserved in place; only the dragged
 * row moves. Synthetic rows (id <= 0: the "Niet toegewezen" row id 0 and the
 * negative EasyKit IDs) are never persisted.
 *
 * @param savedOrder  the current full saved order (may omit currently-visible rows)
 * @param visibleIds  ids of the rows currently rendered (includes drag/target)
 * @param draggedId   the row being moved
 * @param targetId    the row it is dropped before
 */
export function moveInstallerInOrder(
  savedOrder: number[],
  visibleIds: number[],
  draggedId: number,
  targetId: number,
): number[] {
  // Append-only merge: saved order first, then any visible row not yet present,
  // in its incoming order. Excludes id <= 0 so synthetic rows never persist. This
  // guarantees draggedId/targetId (both visible) are present while keeping every
  // off-screen saved installer in place.
  const base: number[] = [];
  const seen = new Set<number>();
  for (const id of [...savedOrder, ...visibleIds]) {
    if (id <= 0 || seen.has(id)) continue;
    seen.add(id);
    base.push(id);
  }

  // Synthetic rows (id <= 0: "Niet toegewezen" and EasyKit sentinels) are never
  // part of the persisted order, so they can't be repositioned. Bail before any
  // insert so a drag from such a row can't leak id 0 / a negative into storage.
  if (draggedId <= 0) return base;

  // Remove the dragged row FIRST, then locate the target in the reduced array, so
  // a downward drag doesn't land one slot too late (stale-index off-by-one).
  const fromIndex = base.indexOf(draggedId);
  if (fromIndex !== -1) base.splice(fromIndex, 1);

  const targetIndex = base.indexOf(targetId);
  if (targetIndex === -1) {
    base.push(draggedId);
  } else {
    base.splice(targetIndex, 0, draggedId);
  }

  return base;
}
